/**
 * Datasets API Service (TASK-288, Epic 29 — Outbound Calling)
 *
 * Thin axios wrapper over the generic Dataset REST surface (TASK-287):
 *   GET/POST          /api/v1/datasets/
 *   GET/PATCH/DELETE  /api/v1/datasets/{id}/
 *   POST              /api/v1/datasets/{id}/upload/        (multipart)
 *   POST              /api/v1/datasets/{id}/upload/headers/ (KAN-727)
 *   GET               /api/v1/datasets/upload-presets/?kind=... (KAN-727)
 *   GET               /api/v1/datasets/{id}/upload-jobs/{job_id}/ (KAN-826)
 *   POST              /api/v1/datasets/{id}/next-batch/    (KAN-850)
 *   POST              /api/v1/datasets/{id}/activate/
 *   POST              /api/v1/datasets/{id}/pause/
 *   GET               /api/v1/datasets/{id}/progress/
 *   GET               /api/v1/datasets/profile-defaults/?codename=...
 *   GET               /api/v1/datasets/profiles/           (plain array)
 *   GET               /api/v1/datasets/outcome-preview/?kind=...
 *   GET               /api/v1/datasets/{dataset_id}/records/
 *   POST              /api/v1/datasets/{dataset_id}/records/{id}/set_state/
 */
import apiClient from './client';
import type {
  CampaignKind,
  ColumnMapping,
  CreateDatasetPayload,
  Dataset,
  DatasetProfile,
  DatasetProgress,
  DatasetRecord,
  OutcomePreview,
  PaginatedDatasetsResponse,
  PaginatedRecordsResponse,
  ProfileDefaults,
  UpdateDatasetPayload,
  UploadHeaders,
  UploadJob,
  UploadOptions,
  UploadPreset,
  UploadResult,
} from '@/types/dataset';

export interface DatasetFilters {
  profile?: string;
  status?: string;
  kind?: CampaignKind;
  page?: number;
  page_size?: number;
}

export interface RecordFilters {
  state?: string;
  page?: number;
  page_size?: number;
}

export const datasetsApi = {
  /** List campaigns (RBAC-scoped server-side). */
  list: async (
    filters?: DatasetFilters
  ): Promise<PaginatedDatasetsResponse> => {
    const { data } = await apiClient.get<PaginatedDatasetsResponse>(
      '/api/v1/datasets/',
      { params: filters }
    );
    return data;
  },

  /** Get a single campaign. */
  get: async (id: string): Promise<Dataset> => {
    const { data } = await apiClient.get<Dataset>(`/api/v1/datasets/${id}/`);
    return data;
  },

  /** Create a campaign. */
  create: async (payload: CreateDatasetPayload): Promise<Dataset> => {
    const { data } = await apiClient.post<Dataset>(
      '/api/v1/datasets/',
      payload
    );
    return data;
  },

  /** Update a campaign. */
  update: async (
    id: string,
    payload: UpdateDatasetPayload
  ): Promise<Dataset> => {
    const { data } = await apiClient.patch<Dataset>(
      `/api/v1/datasets/${id}/`,
      payload
    );
    return data;
  },

  /** Delete a campaign. */
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/datasets/${id}/`);
  },

  /**
   * Upload a CSV/Excel base and import it (multipart).
   *
   * `columnMapping` is the UI convention {schemaField -> sourceColumn}. KAN-727:
   * it is sent as-is in `fields_mapping`, so one column may feed several fields
   * (the old inverted `column_mapping` {sourceColumn -> schemaField} kept only
   * the last field of a shared column).
   */
  upload: async (
    id: string,
    file: File | null,
    columnMapping: ColumnMapping,
    options: UploadOptions = {}
  ): Promise<UploadResult> => {
    const fieldsMapping: ColumnMapping = {};
    Object.entries(columnMapping).forEach(([schemaField, sourceColumn]) => {
      if (sourceColumn) {
        fieldsMapping[schemaField] = sourceColumn;
      }
    });

    // KAN-725: the source is a file OR a Google Sheets link; `limit` caps the
    // NEW rows taken from the file, `skip_existing_clients` drops known clients.
    const formData = new FormData();
    if (file) {
      formData.append('file', file);
    } else if (options.sheetUrl) {
      formData.append('sheet_url', options.sheetUrl);
    }
    formData.append('fields_mapping', JSON.stringify(fieldsMapping));
    if (options.maxRows != null) {
      formData.append('max_rows', String(options.maxRows));
    }
    if (options.limit != null) {
      formData.append('limit', String(options.limit));
    }
    if (options.skipExistingClients) {
      formData.append('skip_existing_clients', 'true');
    }
    // KAN-826: organization columns, custom fields and "supplement existing",
    // JSON strings like fields_mapping; sent only when the form gives them.
    if (options.companyFields) {
      const companyFields: Record<string, string> = {};
      Object.entries(options.companyFields).forEach(([key, column]) => {
        if (column?.trim()) companyFields[key] = column.trim();
      });
      formData.append('company_fields', JSON.stringify(companyFields));
    }
    if (options.extraFields) {
      formData.append('extra_fields', JSON.stringify(options.extraFields));
    }
    if (options.updateExisting !== undefined) {
      formData.append('update_existing', options.updateExisting ? 'true' : 'false');
    }
    if (options.dryRun) {
      formData.append('dry_run', 'true');
    }

    const { data } = await apiClient.post<UploadResult>(
      `/api/v1/datasets/${id}/upload/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },

  /** KAN-727: column headers (+ up to 2 sample rows) of a file or Google Sheet. */
  uploadHeaders: async (
    id: string,
    source: { file?: File | null; sheetUrl?: string }
  ): Promise<UploadHeaders> => {
    const formData = new FormData();
    if (source.file) {
      formData.append('file', source.file);
    } else if (source.sheetUrl) {
      formData.append('sheet_url', source.sheetUrl);
    }
    const { data } = await apiClient.post<UploadHeaders>(
      `/api/v1/datasets/${id}/upload/headers/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },

  /** KAN-727: last remembered upload settings of the org for a list kind. */
  uploadPresets: async (kind?: CampaignKind): Promise<UploadPreset[]> => {
    const { data } = await apiClient.get<UploadPreset[]>(
      '/api/v1/datasets/upload-presets/',
      { params: kind ? { kind } : {} }
    );
    return data;
  },

  /** KAN-826: state of a background job of supplementing uploaded records. */
  uploadJob: async (id: string, jobId: string): Promise<UploadJob> => {
    const { data } = await apiClient.get<UploadJob>(
      `/api/v1/datasets/${id}/upload-jobs/${jobId}/`
    );
    return data;
  },

  /**
   * KAN-850: the next batch of a list: an empty copy with the same kind,
   * owner, schema and upload settings (scheduling off, draft).
   */
  nextBatch: async (id: string): Promise<Dataset> => {
    const { data } = await apiClient.post<Dataset>(
      `/api/v1/datasets/${id}/next-batch/`
    );
    return data;
  },

  /** Enable scheduling (start calling). */
  activate: async (id: string): Promise<Dataset> => {
    const { data } = await apiClient.post<Dataset>(
      `/api/v1/datasets/${id}/activate/`
    );
    return data;
  },

  /** Disable scheduling (pause calling). */
  pause: async (id: string): Promise<Dataset> => {
    const { data } = await apiClient.post<Dataset>(
      `/api/v1/datasets/${id}/pause/`
    );
    return data;
  },

  /** Per-state aggregate progress for a campaign. */
  progress: async (id: string): Promise<DatasetProgress> => {
    const { data } = await apiClient.get<DatasetProgress>(
      `/api/v1/datasets/${id}/progress/`
    );
    return data;
  },

  /** List records of a campaign, optionally filtered by state. */
  listRecords: async (
    datasetId: string,
    filters?: RecordFilters
  ): Promise<PaginatedRecordsResponse> => {
    const { data } = await apiClient.get<PaginatedRecordsResponse>(
      `/api/v1/datasets/${datasetId}/records/`,
      { params: filters }
    );
    return data;
  },

  /** Move a record to a new state (e.g. do_not_call). */
  setRecordState: async (
    datasetId: string,
    recordId: string,
    state: string
  ): Promise<DatasetRecord> => {
    const { data } = await apiClient.post<DatasetRecord>(
      `/api/v1/datasets/${datasetId}/records/${recordId}/set_state/`,
      { state }
    );
    return data;
  },

  /** Default schema + scheduling for a profile codename (and list kind). */
  profileDefaults: async (
    codename: string,
    kind?: CampaignKind
  ): Promise<ProfileDefaults> => {
    const { data } = await apiClient.get<ProfileDefaults>(
      '/api/v1/datasets/profile-defaults/',
      { params: kind ? { codename, kind } : { codename } }
    );
    return data;
  },

  /**
   * What will happen to a list of this kind (KAN-595). KAN-752: ``ownerId`` -
   * the chosen campaign operator of a Telegram list.
   */
  outcomePreview: async (kind: CampaignKind, ownerId?: string): Promise<OutcomePreview> => {
    const { data } = await apiClient.get<OutcomePreview>(
      '/api/v1/datasets/outcome-preview/',
      { params: ownerId ? { kind, owner_id: ownerId } : { kind } }
    );
    return data;
  },

  /**
   * Campaign profiles available for the current org's mode (TASK-338).
   * The backend returns a plain array [{codename, label}] — NOT DRF-paginated.
   */
  profiles: async (): Promise<DatasetProfile[]> => {
    const { data } = await apiClient.get<DatasetProfile[]>(
      '/api/v1/datasets/profiles/'
    );
    return data;
  },
};

export default datasetsApi;
